import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { isForecastEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { FinanceNav } from '@/components/tracking/FinanceNav';
import { useFinancialModel } from '@/hooks/use-financial-model';
import { scenarioOutcomes, latestMetricValue } from '@/lib/financial-calc/forecast';
import { fmtCurrency } from '@/components/tracking/format';
import type { AccountRole } from '@/types/tracking-view';
import { ForecastToolNav } from '@/components/finance/ForecastToolNav';

/**
 * /finance/forecast/what-if-scenarios — the What-If Scenarios tool (F-013 S6.4). Best / base / worst
 * projected 12 months from the real actuals baseline, side by side. Real + flag-gated.
 */
export const Route = createFileRoute('/finance/forecast_/what-if-scenarios')({
  beforeLoad: () => {
    if (!isXeroEnabled() || !isForecastEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
  },
  component: () => (
    <FinanceGuard>
      <WhatIfScenarios />
    </FinanceGuard>
  ),
});

const TONE: Record<string, string> = { best: 'border-success/40', base: 'border-border', worst: 'border-danger/40' };

function WhatIfScenarios() {
  const { model, input } = useFinancialModel();
  const periods = input?.periods ?? [];
  const cur = periods[periods.length - 1]?.roleTotals ?? {};
  const n = (k: AccountRole) => cur[k] ?? 0;
  const margin = latestMetricValue(model?.metricsById['gross-margin']) ?? 25;

  const outcomes = scenarioOutcomes({
    revenueSeries: periods.map((p) => p.roleTotals.revenue ?? 0),
    cashSeries: periods.map((p) => p.roleTotals['cash-bucket'] ?? 0),
    marginPct: margin,
    overhead: n('overhead'),
  });

  return (
    <div className="mx-auto w-full max-w-6xl">
      <FinanceNav />
      <Link to="/finance/forecast" className="mt-2 inline-flex items-center gap-1 text-[12px] text-muted-foreground no-underline hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Forecast
      </Link>
      <h1 className="ds-h1 mt-1">What-If Scenarios</h1>
      <p className="mb-4 mt-0.5 text-[13px] text-muted-foreground">The next 12 months three ways — if things go well, stay flat, or tighten up.</p>

      {!model || periods.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <p className="font-semibold text-foreground">Needs your Xero data</p>
          <p className="mt-1 text-[12.5px]">Connect Xero and sync to model your scenarios.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
          {outcomes.map((o) => (
            <div key={o.key} className={`rounded-2xl border-2 bg-card p-4 ${TONE[o.key]}`}>
              <p className="text-[13px] font-semibold text-foreground">{o.label}{o.key === 'base' && <span className="ml-1 text-[10px] text-muted-foreground">(current trend)</span>}</p>
              <div className="mt-3 flex flex-col gap-2 text-[12.5px]">
                <div className="flex justify-between"><span className="text-muted-foreground">Revenue (12 mo)</span><span className="font-mono tabular-nums">{fmtCurrency(o.revenue12mo)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Net profit</span><span className={`font-mono tabular-nums font-semibold ${o.netProfit12mo >= 0 ? 'text-success' : 'text-danger'}`}>{fmtCurrency(o.netProfit12mo)}</span></div>
                {(() => {
                  const maxAbs = Math.max(...outcomes.map((x) => Math.abs(x.netProfit12mo)), 1);
                  const w = (Math.abs(o.netProfit12mo) / maxAbs) * 100;
                  return (
                    <div className="h-2.5 overflow-hidden rounded bg-muted">
                      <div className={`h-2.5 rounded ${o.netProfit12mo >= 0 ? 'bg-success/70' : 'bg-danger/70'}`} style={{ width: `${w}%` }} />
                    </div>
                  );
                })()}
                <div className="flex justify-between"><span className="text-muted-foreground">Lowest cash</span><span className={`font-mono tabular-nums ${o.lowestCash < 0 ? 'text-danger' : 'text-foreground'}`}>{fmtCurrency(o.lowestCash)}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-4 text-[11px] text-muted-foreground">Each scenario shifts growth, margin and overheads off your real baseline. Steer the exact levers on the Forecast page.</p>
      <ForecastToolNav current="what-if-scenarios" />
    </div>
  );
}
